import React from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  ArrowUpRight,
  ArrowDownLeft,
  History,
  Package,
  Calendar,
  ExternalLink,
  User,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import apiClient from "../../../api/apiClient";
import { API_ENDPOINTS } from "../../../api/header";
import { all_routes } from "../../../Router/all_routes";
import { formatDateTime } from "@/lib/utils";

const TYPE_CONFIG = {
  receipt: { label: "Receipt", variant: "success", isIn: true },
  purchase: { label: "Purchase", variant: "success", isIn: true },
  opening_stock: { label: "Opening Stock", variant: "success", isIn: true },
  adjustment_in: { label: "Adjustment In", variant: "success", isIn: true },
  transfer_in: { label: "Transfer In", variant: "success", isIn: true },
  return: { label: "Return", variant: "secondary", isIn: true },
  return_in: { label: "Return In", variant: "secondary", isIn: true },
  sale: { label: "Sale", variant: "info", isIn: false },
  adjustment_out: { label: "Adjustment Out", variant: "warning", isIn: false },
  transfer_out: { label: "Transfer Out", variant: "destructive", isIn: false },
  adjustment: { label: "Adjustment", variant: "warning", isIn: null },
  count: { label: "Stock Count", variant: "outline", isIn: null },
};

const REFERENCE_LABELS = {
  goods_received_note_item: "GRN Item",
  goods_received_note: "GRN",
  sale_item: "Sale Item",
  sale: "Sale",
  stock_adjustment: "Adjustment",
  stock_adjustment_item: "Adjustment Item",
  repair_part: "Repair Part",
  stock_transfer: "Transfer",
};

const TransactionHistoryModal = ({ show, onHide, inventoryItem }) => {
  const navigate = useNavigate();
  const inventoryId = inventoryItem?.id;

  // 1. Fetch Summary (GET /v1/inventory-transactions/summary/{inventory})
  const { data: summaryResponse, isLoading: summaryLoading } = useQuery({
    queryKey: ["inventorySummary", inventoryId],
    queryFn: async () => {
      const res = await apiClient.get(
        API_ENDPOINTS.INVENTORY.TRANSACTIONS_SUMMARY(inventoryId),
      );
      return res.data;
    },
    enabled: !!inventoryId && show,
  });

  // 2. Fetch full transactions list (GET /v1/inventory-transactions?inventory_id={id})
  const { data: transactionsData, isLoading: transactionsLoading } = useQuery({
    queryKey: ["inventoryTransactions", inventoryId],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.TRANSACTIONS, {
        params: {
          inventory_id: inventoryId,
          per_page: 50,
          with: "creator,user",
        },
      });
      return res.data?.data || [];
    },
    enabled: !!inventoryId && show,
  });

  const summary = summaryResponse?.data || summaryResponse?.summary || {};
  const byType = summary.by_type || {};

  // Compute movement metrics from by_type and summary
  const totalIn =
    (byType.receipt?.total_quantity || 0) +
    (byType.transfer_in?.total_quantity || 0) +
    (byType.return?.total_quantity || 0) +
    (byType.opening_stock?.total_quantity || 0);

  const inCount =
    (byType.receipt?.transaction_count || 0) +
    (byType.transfer_in?.transaction_count || 0) +
    (byType.return?.transaction_count || 0) +
    (byType.opening_stock?.transaction_count || 0);

  const totalOut = Math.abs(
    (byType.sale?.total_quantity || 0) +
    (byType.transfer_out?.total_quantity || 0),
  );

  const outCount =
    (byType.sale?.transaction_count || 0) +
    (byType.transfer_out?.transaction_count || 0);

  const netAdjustments =
    (byType.adjustment?.total_quantity || 0) +
    (byType.count?.total_quantity || 0);

  const adjCount =
    (byType.adjustment?.transaction_count || 0) +
    (byType.count?.transaction_count || 0);

  const currentStock = summary.current_quantity ?? summary.balance ?? 0;
  const totalLogs =
    summary.total_transactions ?? transactionsData?.length ?? 0;

  // Transactions list with fallback to summary.recent_transactions
  const transactions =
    transactionsData && transactionsData.length > 0
      ? transactionsData
      : summary.recent_transactions || [];

  const getTransactionIcon = (type, quantity) => {
    const config = TYPE_CONFIG[type];
    const isIn = config?.isIn ?? quantity > 0;
    return isIn ? (
      <div className="p-1.5 rounded-full bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-400">
        <ArrowDownLeft className="h-3.5 w-3.5" />
      </div>
    ) : (
      <div className="p-1.5 rounded-full bg-rose-100 dark:bg-rose-950/60 text-rose-700 dark:text-rose-400">
        <ArrowUpRight className="h-3.5 w-3.5" />
      </div>
    );
  };

  const getTransactionBadge = (type) => {
    const config = TYPE_CONFIG[type] || {
      label: type
        ? type.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
        : "Adjustment",
      variant: "secondary",
    };
    return (
      <Badge variant={config.variant} className="text-[11px] font-medium">
        {config.label}
      </Badge>
    );
  };

  const productId =
    inventoryItem?.product_id || inventoryItem?.product?.id || null;

  return (
    <Dialog open={show} onOpenChange={(open) => !open && onHide()}>
      <DialogContent className="max-w-3xl p-0 flex flex-col max-h-[90vh] overflow-hidden">
        <DialogHeader className="px-6 py-4 border-b flex-shrink-0 bg-muted/20">
          <div className="flex items-center gap-2">
            <History className="h-5 w-5 text-primary" />
            <DialogTitle className="text-lg font-bold">
              Inventory Movement History
            </DialogTitle>
          </div>
          <DialogDescription className="text-xs text-muted-foreground mt-1">
            Tracking movements for{" "}
            <span className="font-semibold text-foreground">
              {inventoryItem?.product?.name || "Product"}
            </span>{" "}
            at{" "}
            <span className="font-medium text-foreground">
              {inventoryItem?.store?.name || "Store"}
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col">
          {summaryLoading || transactionsLoading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Spinner className="h-9 w-9 mb-3 text-primary" />
              <p className="text-sm text-muted-foreground animate-pulse">
                Loading inventory history...
              </p>
            </div>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 bg-muted/10 shrink-0 border-b">
                <div className="bg-card p-3 rounded-lg border shadow-sm flex flex-col items-center text-center">
                  <span className="text-[10px] uppercase font-bold text-muted-foreground">
                    Current Stock
                  </span>
                  <span className="text-xl font-bold text-primary mt-1">
                    {currentStock}
                  </span>
                  <span className="text-[10px] text-muted-foreground mt-0.5">
                    {totalLogs} total events
                  </span>
                </div>

                <div className="bg-card p-3 rounded-lg border shadow-sm flex flex-col items-center text-center">
                  <span className="text-[10px] uppercase font-bold text-emerald-600 dark:text-emerald-400">
                    Total Inflow
                  </span>
                  <span className="text-xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">
                    +{totalIn}
                  </span>
                  <span className="text-[10px] text-muted-foreground mt-0.5">
                    {inCount} receipts/transfers
                  </span>
                </div>

                <div className="bg-card p-3 rounded-lg border shadow-sm flex flex-col items-center text-center">
                  <span className="text-[10px] uppercase font-bold text-rose-600 dark:text-rose-400">
                    Total Outflow
                  </span>
                  <span className="text-xl font-bold text-rose-600 dark:text-rose-400 mt-1">
                    -{totalOut}
                  </span>
                  <span className="text-[10px] text-muted-foreground mt-0.5">
                    {outCount} sales/transfers
                  </span>
                </div>

                <div className="bg-card p-3 rounded-lg border shadow-sm flex flex-col items-center text-center">
                  <span className="text-[10px] uppercase font-bold text-amber-600 dark:text-amber-400">
                    Adjustments
                  </span>
                  <span className="text-xl font-bold text-amber-600 dark:text-amber-400 mt-1">
                    {netAdjustments > 0 ? `+${netAdjustments}` : netAdjustments}
                  </span>
                  <span className="text-[10px] text-muted-foreground mt-0.5">
                    {adjCount} adjustments
                  </span>
                </div>
              </div>

              {/* Transactions Timeline */}
              <ScrollArea className="flex-1 p-6">
                <div className="space-y-6 relative ml-4 border-l pl-8 pb-4">
                  {transactions.length > 0 ? (
                    transactions.map((tx, idx) => {
                      const itemType = tx.type || tx.transaction_type;
                      const creatorName =
                        tx.creator?.username ||
                        tx.creator?.name ||
                        tx.user?.username ||
                        tx.user?.name ||
                        "System";
                      const refLabel =
                        REFERENCE_LABELS[tx.reference_type] ||
                        tx.reference_no ||
                        (tx.reference_type
                          ? tx.reference_type.replace(/_/g, " ")
                          : null);
                      const metaDelta =
                        tx.meta?.old_quantity !== undefined &&
                          tx.meta?.new_quantity !== undefined
                          ? `${tx.meta.old_quantity} → ${tx.meta.new_quantity}`
                          : null;

                      return (
                        <div key={tx.id || idx} className="relative">
                          {/* Dot on line */}
                          <div className="absolute -left-[41px] top-0 bg-card rounded-full border-2 border-border p-0.5">
                            {getTransactionIcon(itemType, tx.quantity)}
                          </div>

                          <div className="flex flex-col gap-1.5">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                {getTransactionBadge(itemType)}
                                {refLabel && (
                                  <Badge
                                    variant="outline"
                                    className="text-[10px] h-5 font-mono"
                                  >
                                    {refLabel}
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-baseline gap-2">
                                {metaDelta && (
                                  <span className="text-xs text-muted-foreground font-mono">
                                    ({metaDelta})
                                  </span>
                                )}
                                <span
                                  className={`text-sm font-bold font-mono ${tx.quantity > 0
                                      ? "text-emerald-600 dark:text-emerald-400"
                                      : tx.quantity < 0
                                        ? "text-rose-600 dark:text-rose-400"
                                        : "text-muted-foreground"
                                    }`}
                                >
                                  {tx.quantity > 0 ? `+${tx.quantity}` : tx.quantity}
                                </span>
                              </div>
                            </div>

                            <div className="flex items-center gap-4 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {formatDateTime(tx.created_at)}
                              </span>
                              <span className="flex items-center gap-1">
                                <User className="h-3 w-3" />
                                By: {creatorName}
                              </span>
                            </div>

                            {tx.notes && (
                              <p className="text-xs italic bg-muted/40 p-2 rounded mt-1 border border-dashed text-foreground/80">
                                &quot;{tx.notes}&quot;
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-muted-foreground ml-[-2rem]">
                      <Package className="h-10 w-10 mb-2 opacity-50" />
                      <p className="text-sm">No transaction history found</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </div>
          )}
        </div>

        <DialogFooter className="px-6 py-3 border-t bg-muted/20 shrink-0 flex flex-row items-center justify-between sm:justify-between">
          {productId ? (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 text-xs"
              onClick={() => {
                onHide();
                navigate(all_routes.editproduct.replace(":id", productId));
              }}
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Manage on Edit Product
            </Button>
          ) : (
            <div />
          )}
          <Button variant="outline" size="sm" onClick={onHide}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

TransactionHistoryModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  inventoryItem: PropTypes.object,
};

export default TransactionHistoryModal;
